import React, { useState, useEffect, useCallback } from 'react';
import { getAlerts, createAlert, deleteAlert, deactivateAlert } from '../../api/client';

// ── Common Indian market symbols (Fyers format) ────────────────────────────
const SYMBOL_LIST = [
  // Indices
  'NSE:NIFTY50-INDEX', 'NSE:NIFTYBANK-INDEX', 'NSE:MIDCPNIFTY-INDEX',
  'NSE:NIFTYIT-INDEX',  'NSE:NIFTYPSE-INDEX',  'BSE:SENSEX-INDEX',
  // Nifty 50 large-caps
  'NSE:RELIANCE-EQ',  'NSE:TCS-EQ',       'NSE:HDFCBANK-EQ',  'NSE:INFY-EQ',
  'NSE:ICICIBANK-EQ', 'NSE:HINDUNILVR-EQ','NSE:BAJFINANCE-EQ','NSE:SBIN-EQ',
  'NSE:BHARTIARTL-EQ','NSE:KOTAKBANK-EQ', 'NSE:LT-EQ',        'NSE:AXISBANK-EQ',
  'NSE:ASIANPAINT-EQ','NSE:MARUTI-EQ',    'NSE:TITAN-EQ',     'NSE:SUNPHARMA-EQ',
  'NSE:WIPRO-EQ',     'NSE:ULTRACEMCO-EQ','NSE:NESTLEIND-EQ', 'NSE:POWERGRID-EQ',
  'NSE:NTPC-EQ',      'NSE:TECHM-EQ',     'NSE:HCLTECH-EQ',   'NSE:BAJAJFINSV-EQ',
  'NSE:TATAMOTORS-EQ','NSE:TATASTEEL-EQ', 'NSE:ADANIENT-EQ',  'NSE:ADANIPORTS-EQ',
  'NSE:JSWSTEEL-EQ',  'NSE:ONGC-EQ',      'NSE:COALINDIA-EQ', 'NSE:GRASIM-EQ',
  'NSE:CIPLA-EQ',     'NSE:DRREDDY-EQ',   'NSE:DIVISLAB-EQ',  'NSE:BRITANNIA-EQ',
  'NSE:EICHERMOT-EQ', 'NSE:HEROMOTOCO-EQ','NSE:M&M-EQ',       'NSE:TATACONSUM-EQ',
  'NSE:BPCL-EQ',      'NSE:IOC-EQ',       'NSE:INDUSINDBK-EQ','NSE:SHRIRAMFIN-EQ',
  'NSE:APOLLOHOSP-EQ','NSE:BAJAJ-AUTO-EQ',
];

const CONDITION_LABEL = { above: '▲ Above', below: '▼ Below' };

function AlertForm({ onCreated }) {
  const [symbol, setSymbol]       = useState('');
  const [condition, setCondition] = useState('above');
  const [price, setPrice]         = useState('');
  const [note, setNote]           = useState('');
  const [busy, setBusy]           = useState(false);
  const [error, setError]         = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!symbol.trim() || !price) return;
    setBusy(true);
    setError('');
    try {
      await createAlert({ symbol: symbol.trim().toUpperCase(), condition, price: parseFloat(price), note: note.trim() || null });
      setSymbol(''); setPrice(''); setNote('');
      onCreated();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create alert');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <label style={{ fontSize: 11, color: 'var(--muted)' }}>Symbol</label>
        <input
          className="input"
          list="symbol-suggestions"
          placeholder="Type or search (e.g. NIFTY, SBIN…)"
          value={symbol}
          onChange={e => setSymbol(e.target.value.toUpperCase())}
          style={{ width: 240 }}
          autoComplete="off"
          required
        />
        <datalist id="symbol-suggestions">
          {SYMBOL_LIST.map(s => <option key={s} value={s} />)}
        </datalist>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <label style={{ fontSize: 11, color: 'var(--muted)' }}>Condition</label>
        <select
          className="input"
          value={condition}
          onChange={e => setCondition(e.target.value)}
          style={{ width: 120 }}
        >
          <option value="above">▲ Above</option>
          <option value="below">▼ Below</option>
        </select>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <label style={{ fontSize: 11, color: 'var(--muted)' }}>Price (₹)</label>
        <input
          className="input"
          type="number"
          placeholder="22500"
          value={price}
          onChange={e => setPrice(e.target.value)}
          style={{ width: 130 }}
          required
        />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <label style={{ fontSize: 11, color: 'var(--muted)' }}>Note (optional)</label>
        <input
          className="input"
          placeholder="Breakout level"
          value={note}
          onChange={e => setNote(e.target.value)}
          style={{ width: 180 }}
        />
      </div>
      <button className="btn btn-primary" type="submit" disabled={busy}>
        {busy ? 'Adding…' : '+ Add Alert'}
      </button>
      {error && <span style={{ color: 'var(--red)', fontSize: 12 }}>{error}</span>}
    </form>
  );
}

function AlertRow({ alert, onRefresh }) {
  const [busy, setBusy] = useState(false);

  const handleDelete = async () => {
    setBusy(true);
    try { await deleteAlert(alert.id); onRefresh(); } finally { setBusy(false); }
  };

  const handleDeactivate = async () => {
    setBusy(true);
    try { await deactivateAlert(alert.id); onRefresh(); } finally { setBusy(false); }
  };

  const statusColor = alert.triggered
    ? 'var(--green)'
    : alert.active
      ? 'var(--accent)'
      : 'var(--muted)';

  const statusLabel = alert.triggered ? '✅ Triggered' : alert.active ? '🟢 Active' : '⏸ Paused';

  return (
    <tr style={{ borderBottom: '1px solid var(--border)' }}>
      <td style={{ padding: '10px 12px', fontWeight: 600, color: 'var(--text)', fontFamily: 'monospace', fontSize: 13 }}>
        {alert.symbol}
      </td>
      <td style={{ padding: '10px 12px', color: alert.condition === 'above' ? 'var(--green)' : 'var(--red)', fontWeight: 600 }}>
        {CONDITION_LABEL[alert.condition]}
      </td>
      <td style={{ padding: '10px 12px', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
        ₹{parseFloat(alert.price).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
      </td>
      <td style={{ padding: '10px 12px', color: 'var(--muted)', fontSize: 12 }}>
        {alert.note || '—'}
      </td>
      <td style={{ padding: '10px 12px' }}>
        <span style={{ fontSize: 12, color: statusColor }}>{statusLabel}</span>
        {alert.triggered_at && (
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
            @ ₹{parseFloat(alert.triggered_at).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </div>
        )}
      </td>
      <td style={{ padding: '10px 12px' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {alert.active && !alert.triggered && (
            <button
              className="btn btn-ghost"
              style={{ fontSize: 11, padding: '3px 8px' }}
              onClick={handleDeactivate}
              disabled={busy}
            >
              Pause
            </button>
          )}
          <button
            className="btn btn-ghost"
            style={{ fontSize: 11, padding: '3px 8px', color: 'var(--red)' }}
            onClick={handleDelete}
            disabled={busy}
          >
            Delete
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function PriceAlerts() {
  const [alerts, setAlerts]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showActive, setShowActive] = useState(false);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await getAlerts(showActive);
      setAlerts(res.data.alerts || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [showActive]);

  useEffect(() => {
    fetchAlerts();
    const t = setInterval(fetchAlerts, 10000);
    return () => clearInterval(t);
  }, [fetchAlerts]);

  const active   = alerts.filter(a => a.active && !a.triggered).length;
  const triggered = alerts.filter(a => a.triggered).length;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 className="page-title" style={{ margin: 0 }}>🔔 Price Alerts</h1>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: 'var(--muted)' }}>
            <span style={{ color: 'var(--accent)', fontWeight: 700 }}>{active}</span> active
            &nbsp;·&nbsp;
            <span style={{ color: 'var(--green)', fontWeight: 700 }}>{triggered}</span> triggered
          </span>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--muted)', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showActive}
              onChange={e => setShowActive(e.target.checked)}
            />
            Active only
          </label>
        </div>
      </div>

      {/* Create form */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ fontWeight: 600, marginBottom: 14 }}>New Alert</div>
        <AlertForm onCreated={fetchAlerts} />
      </div>

      {/* Alerts table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 20, color: 'var(--muted)' }}>Loading alerts…</div>
        ) : alerts.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted)' }}>
            No alerts yet. Add one above to get notified when a price level is reached.
          </div>
        ) : (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ width: '100%', minWidth: 600, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg)' }}>
                {['Symbol', 'Condition', 'Target Price', 'Note', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {alerts.map(alert => (
                <AlertRow key={alert.id} alert={alert} onRefresh={fetchAlerts} />
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
